import { Box, ListItem } from "@mui/material";
import * as React from "react";
import {
  apiPost,
  buildFilterString,
  buildSearchString,
  fetchData,
} from "../../common/services/ApiClient";
import { chunkArray, reduceObj } from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import { FormSelectChoice, KeyValue } from "../../interfaces";
import SelectFilterField from "../SelectFilterField";
import MenuCheckForm from "./MenuCheckForm";

interface Props {
  filtersAndSearch: KeyValue;
  currentIndex: number;
  searchForm: KeyValue;
  setSearchForm: (form: any) => void;
  errors: boolean[];
  setErrors: (errors: boolean[]) => void;
  disableAll?: boolean;
  institutionType?: string;
}

export default function BranchOfficeMarketSelections({
  filtersAndSearch,
  currentIndex,
  searchForm,
  setSearchForm,
  errors,
  setErrors,
  disableAll,
  institutionType,
}: Props) {
  const [counties, setCounties] = React.useState([]);
  const [stateSelections, setStateSelections] = React.useState<
    FormSelectChoice[]
  >([]);
  const [countySelection, setCountySelection] = React.useState<KeyValue>({});
  const [citySelection, setCitySelection] = React.useState<KeyValue>({});
  const [zipSelection, setZipSelection] = React.useState<KeyValue>({});
  const [cities, setCities] = React.useState([]);
  const [zips, setZips] = React.useState([]);
  const [currentInstitutionType, setCurrentInstitutionType] =
    React.useState("");

  const [certUninums, setCertUninums] = React.useState<KeyValue>({});
  React.useEffect(() => {
    const stateValues = stateSelections.map((state) => state.value);
    if (
      stateValues.length &&
      searchForm.stateNames.length &&
      !searchForm.stateNames.every((state: string) =>
        stateValues.includes(state),
      )
    ) {
      setSearchForm({ ...searchForm, stateNames: [""], marketSelection: [{}] });
    }
  }, [searchForm, searchForm.stateNames, setSearchForm, stateSelections]);

  const countyCityZip = React.useMemo(() => {
    return {
      counties: Object.keys(countySelection).filter(
        (county: string) => countySelection[county],
      ),
      cities: Object.keys(citySelection).filter(
        (city: string) => citySelection[city],
      ),
      zips: Object.keys(zipSelection).filter(
        (zip: string) => zipSelection[zip],
      ),
    };
  }, [citySelection, countySelection, zipSelection]);
  const [allValues, setAllValues] = React.useState<KeyValue>(countyCityZip);
  const marketSelectionErrors = React.useMemo(() => {
    return {
      counties:
        Object.values(countySelection).every((county) => county === false) &&
        !(counties.length < 1),
      cities:
        Object.values(citySelection).every((city) => city === false) &&
        !(cities.length < 1),
      zips:
        Object.values(zipSelection).every((zip) => zip === false) &&
        !(zips.length < 1),
    };
  }, [
    cities.length,
    citySelection,
    counties.length,
    countySelection,
    zipSelection,
    zips.length,
  ]);

  const [marketSelectionState, setMarketSelectionState] =
    React.useState<KeyValue>({});

  React.useEffect(() => {
    const countyKeys = Object.keys(countySelection).filter((key) => {
      return countySelection[key];
    });
    const cityKeys = Object.keys(citySelection).filter((key) => {
      return citySelection[key];
    });
    const zipKeys = Object.keys(zipSelection).filter((key) => {
      return zipSelection[key];
    });

    const newMarketSelect = {
      counties:
        (countyKeys.length === counties.length && counties.length > 0) ||
        countyKeys.length === 0
          ? ["All"]
          : countyKeys,
      cities:
        (cityKeys.length === cities.length && cities.length > 0) ||
        cityKeys.length === 0
          ? ["All"]
          : cityKeys,
      zips:
        (zipKeys.length === zips.length && zips.length > 0) ||
        zipKeys.length === 0
          ? ["All"]
          : zipKeys,
      certUninums,
    };
    if (
      JSON.stringify(newMarketSelect) !== JSON.stringify(marketSelectionState)
    ) {
      setMarketSelectionState(newMarketSelect);
      if (!searchForm.marketSelection[currentIndex]) {
        setSearchForm({
          ...searchForm,
          marketSelection: [...searchForm.marketSelection, newMarketSelect],
        });
      }
      if (
        searchForm.marketSelection[currentIndex] &&
        JSON.stringify(searchForm.marketSelection[currentIndex]) !==
          JSON.stringify(newMarketSelect)
      ) {
        setSearchForm({
          ...searchForm,
          marketSelection: searchForm.marketSelection.map(
            (selection: KeyValue, index: number) => {
              if (currentIndex === index) {
                return newMarketSelect;
              } else {
                return selection;
              }
            },
          ),
        });
      }
    }
  }, [
    certUninums,
    cities.length,
    citySelection,
    counties.length,
    countySelection,
    currentIndex,
    marketSelectionState,
    searchForm,
    setSearchForm,
    zipSelection,
    zips.length,
  ]);

  const [previousFiltersSearch, setPreviousFiltersSearch] =
    React.useState<KeyValue>();

  const [makeCountiesCall, setMakeCountiesCall] = React.useState(false);
  React.useEffect(() => {
    const filtersSearch = {
      filters: filtersAndSearch.filters,
      search: filtersAndSearch.search,
      disabled: disableAll,
    };

    if (
      JSON.stringify(filtersSearch) !== JSON.stringify(previousFiltersSearch)
    ) {
      setPreviousFiltersSearch(filtersSearch);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=${buildFilterString(
            filtersAndSearch.filters,
          )}&search=${buildSearchString(
            filtersAndSearch.search,
          )}&agg_by=STNAMEBR&agg_term_fields=STALPBR&agg_limit=10000`,
        )
          .then((response) => {
            if (isMounted) {
              const allStates = response.data.map((e: KeyValue) => {
                const stalpbrKey = Object.keys(e).find((key: string) =>
                  key.includes("STALPBR"),
                );
                return {
                  label: e.STNAMEBR,
                  value: stalpbrKey ? stalpbrKey.split("_")[2] : e.STNAMEBR,
                };
              });
              const stateSelection =
                searchForm.stateNames.length === 0 || disableAll
                  ? [
                      { label: "Select States and Territories", value: "" },

                      ...allStates,
                    ]
                  : [
                      { label: "Select States and Territories", value: "" },
                      { label: "All States and Territories", value: "ALL" },

                      ...allStates,
                    ];
              if (
                JSON.stringify(stateSelection) !==
                JSON.stringify(stateSelections)
              ) {
                setStateSelections(stateSelection);
              }
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            setMakeCountiesCall(true);
          });
      })();
    }
  }, [
    filtersAndSearch.filters,
    filtersAndSearch.search,
    previousFiltersSearch,
    searchForm.stateNames,
    disableAll,
    stateSelections,
  ]);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !errors[currentIndex] &&
        Object.values(marketSelectionErrors).includes(true) &&
        [...counties, ...cities, ...zips].length > 0
      ) {
        setErrors(
          errors.map((error: boolean, index: number) =>
            index === currentIndex ? true : error,
          ),
        );
      } else if (
        errors[currentIndex] &&
        !Object.values(marketSelectionErrors).includes(true) &&
        [...counties, ...cities, ...zips].length > 0
      ) {
        setErrors(
          errors.map((error: boolean, index: number) =>
            index === currentIndex ? false : error,
          ),
        );
      }
    }, 20);
    return () => clearTimeout(timeout);
  }, [
    cities,
    counties,
    currentIndex,
    errors,
    marketSelectionErrors,
    setErrors,
    zips,
  ]);

  const [previousSelectedCounties, setPreviousSelectedCounties] =
    React.useState<string[]>([]);

  const [previousSelectedCities, setPreviousSelectedCities] = React.useState<
    string[]
  >([]);

  React.useEffect(() => {
    if (
      JSON.stringify(Object.keys(countySelection)) !== JSON.stringify(counties)
    ) {
      const allSelected = Object.keys(countySelection).every(
        (county) => countySelection[county] === true,
      );
      setCountySelection((prev) =>
        reduceObj(
          counties.map((val: string) => {
            return {
              key: val,
              value: allSelected ? true : prev[val] || false,
            };
          }),
        ),
      );
    }
    if (JSON.stringify(Object.keys(citySelection)) !== JSON.stringify(cities)) {
      setCitySelection(
        reduceObj(
          cities.map((val: string) => {
            return {
              key: val,
              value:
                citySelection[val] !== undefined ? citySelection[val] : true,
            };
          }),
        ),
      );
    }
    if (
      JSON.stringify(Object.keys(zipSelection).sort()) !==
      JSON.stringify(zips.sort())
    ) {
      setZipSelection(
        reduceObj(
          zips.map((val: string) => {
            return {
              key: val,
              value: zipSelection[val] !== undefined ? zipSelection[val] : true,
            };
          }),
        ),
      );
    }
  }, [cities, citySelection, counties, countySelection, zipSelection, zips]);

  const handleCountyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCountySelection({
      ...countySelection,
      [event.target.name]: event.target.checked,
    });
  };
  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCitySelection({
      ...citySelection,
      [event.target.name]: event.target.checked,
    });
  };
  const handleZipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZipSelection({
      ...zipSelection,
      [event.target.name]: event.target.checked,
    });
  };

  React.useEffect(() => {
    if (searchForm.stateNames[currentIndex] && makeCountiesCall) {
      setMakeCountiesCall(false);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=STALPBR:"${
            searchForm.stateNames[currentIndex]
          }" AND ${buildFilterString(
            filtersAndSearch.filters,
          )}&search=${buildSearchString(
            filtersAndSearch.search,
          )}&agg_by=CNTYNAMB&agg_limit=10000`,
        )
          .then((response) => {
            if (isMounted) {
              const allCounties = response.data.map(
                (e: KeyValue) => e.CNTYNAMB,
              );
              setCounties(allCounties);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    filtersAndSearch.filters,
    filtersAndSearch.search,
    currentIndex,
    searchForm.stateNames,
    makeCountiesCall,
  ]);

  React.useEffect(() => {
    const filteredCounties = Object.keys(countySelection).filter(
      (county) => countySelection[county] && county,
    );
    if (
      filteredCounties.length > 0 &&
      JSON.stringify(filteredCounties) !==
        JSON.stringify(previousSelectedCounties)
    ) {
      setPreviousSelectedCounties(filteredCounties);
      const countiesPostReq = apiPost({
        agg_limit: 10000,
        agg_by: "CITY2BR",
        search: buildSearchString(filtersAndSearch.search),
        filters: `STALPBR:${
          searchForm.stateNames[currentIndex]
        } AND CNTYNAMB:(${
          filteredCounties.length > 0
            ? filteredCounties.map((county) => `"${county}"`).join(" OR ")
            : "undefined"
        }) AND ${buildFilterString(filtersAndSearch.filters)}`,
      });
      let isMounted = true;
      (async () => {
        await fetchData(`${ApiBaseUrl()}/sod`, countiesPostReq)
          .then((response) => {
            if (isMounted) {
              const allCities = response.data.map((e: KeyValue) => e.CITY2BR);
              setCities(allCities);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    filtersAndSearch.filters,
    filtersAndSearch.search,
    countySelection,
    currentIndex,
    previousSelectedCounties,
    searchForm.stateNames,
  ]);
  React.useEffect(() => {
    const filteredCities = Object.keys(citySelection).filter(
      (city) => citySelection[city] && city,
    );
    if (
      filteredCities.length > 0 &&
      JSON.stringify(filteredCities) !== JSON.stringify(previousSelectedCities)
    ) {
      setPreviousSelectedCities(filteredCities);
      let isMounted = true;
      const zipPostReq = apiPost({
        agg_limit: 10000,
        agg_by: "ZIPBR",
        search: buildSearchString(filtersAndSearch.search),
        filters: `STALPBR:${searchForm.stateNames[currentIndex]} AND CITY2BR:(${
          filteredCities.length > 0
            ? filteredCities.map((city) => `"${city}"`).join(" OR ")
            : "undefined"
        }) AND  ${buildFilterString(filtersAndSearch.filters)}`,
      });
      (async () => {
        await fetchData(`${ApiBaseUrl()}/sod`, zipPostReq)
          .then((response) => {
            if (isMounted) {
              const allZips = response.data.map((e: KeyValue) => e.ZIPBR);
              setZips(allZips);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    filtersAndSearch.filters,
    filtersAndSearch.search,
    citySelection,
    currentIndex,
    previousSelectedCities,
    searchForm.stateNames,
  ]);

  React.useEffect(() => {
    if (
      !searchForm.stateNames[0] &&
      ![
        ...Object.values(countySelection),
        ...Object.values(citySelection),
        ...Object.values(zipSelection),
      ].every((val: boolean) => val)
    ) {
      setCountySelection({});
      setCitySelection({});
      setZipSelection({});
    }
  }, [citySelection, countySelection, searchForm.stateNames, zipSelection]);

  React.useEffect(() => {
    let isMounted = true;
    if (
      (JSON.stringify(allValues) !== JSON.stringify(countyCityZip) ||
        institutionType !== currentInstitutionType) &&
      searchForm.stateNames[currentIndex] &&
      institutionType
    ) {
      const filteredCounties = countyCityZip.counties.filter(
        (county: string) => county && county !== "All",
      );
      const filteredCities = countyCityZip.cities.filter(
        (city: string) => city && city !== "All",
      );
      const filteredZips = countyCityZip.zips.filter(
        (zip: string) => zip && zip !== "All",
      );

      let countyCityFilters = "";

      let zipArray: any[] = [];

      if (filteredCounties.length > 0) {
        countyCityFilters = `${countyCityFilters}  AND CNTYNAMB:(${filteredCounties
          .map((county: string) => `"${county}"`)
          .join(" OR ")})`;
      }
      if (filteredCities.length > 0) {
        countyCityFilters = `${countyCityFilters}  AND CITY2BR:(${filteredCities
          .map((city: string) => `"${city}"`)
          .join(" OR ")})`;
      }
      if (filteredZips.length > 0) {
        if (filteredZips.length > 500) {
          zipArray = chunkArray(filteredZips, 500);
        } else {
          zipArray = [filteredZips];
        }
      }
      setCurrentInstitutionType(institutionType);
      setAllValues(countyCityZip);

      const uniquePostReq = zipArray.map((zips: any) =>
        fetchData(
          `${ApiBaseUrl()}/sod`,
          apiPost({
            limit: 10000,
            search: buildSearchString(filtersAndSearch.search),
            filters: `STALPBR:${
              searchForm.stateNames[currentIndex]
            } AND ${buildFilterString(
              filtersAndSearch.filters,
            )}${countyCityFilters} AND ZIPBR:(${zips.join(" OR ")})`,
          }),
        ),
      );
      (async () => {
        await Promise.all(uniquePostReq)
          .then((response) => {
            if (isMounted) {
              const data = response.map((res: any) => res.data).flat();

              const certUninum: KeyValue = {};

              data.forEach((val) => {
                if (institutionType === "bhcs") {
                  if (val.RSSDHCR > 0) {
                    certUninum[val.RSSDHCR] = [
                      ...(certUninum[val.RSSDHCR] || []),
                      val.UNINUMBR,
                    ];
                  }
                } else {
                  certUninum[val.CERT] = [
                    ...(certUninum[val.CERT] || []),
                    val.UNINUMBR,
                  ];
                }
              });
              setCertUninums(certUninum);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    allValues,
    countyCityZip,
    currentIndex,
    currentInstitutionType,
    filtersAndSearch.filters,
    filtersAndSearch.search,
    institutionType,
    searchForm.stateNames,
  ]);

  return (
    <ListItem>
      <SelectFilterField
        disabledOptions={searchForm.stateNames}
        currentValue={searchForm.stateNames[currentIndex]}
        fieldName="marketSelect"
        options={stateSelections}
        onChange={(e: KeyValue) => {
          setSearchForm({
            ...searchForm,
            stateNames: searchForm.stateNames.map(
              (state: string, int: number) => {
                return int === currentIndex ? e.target.value : state;
              },
            ),
          });
        }}
      />
      {(searchForm.stateNames &&
        searchForm.stateNames[currentIndex] &&
        searchForm.stateNames[currentIndex] !== "ALL") ||
      undefined ? (
        <>
          <Box
            style={{
              marginTop: -30,
              borderBottomRightRadius: 5,
              borderBottomLeftRadius: 5,
            }}
            sx={{
              bgcolor: "#ecf3fb",
              border: 1,
              marginTop: -1,
              justifyContent: "center",
              borderColor: "#ACC8DF",
              paddingTop: 1,
              marginBottom: 2,
            }}
          >
            <MenuCheckForm
              type={"Counties"}
              options={counties}
              onChange={handleCountyChange}
              checkedStatus={countySelection}
              setCheckedStatus={setCountySelection}
              // disabled={counties.length < 1}
              error={marketSelectionErrors.counties}
              pageSize={48}
            />
            <MenuCheckForm
              type={"Cities"}
              options={cities}
              onChange={handleCityChange}
              checkedStatus={citySelection}
              setCheckedStatus={setCitySelection}
              disabled={cities.length < 1}
              error={marketSelectionErrors.cities}
              pageSize={48}
            />
            <MenuCheckForm
              type={"Zip Codes"}
              options={zips}
              onChange={handleZipChange}
              checkedStatus={zipSelection}
              setCheckedStatus={setZipSelection}
              disabled={zips.length < 1}
              error={marketSelectionErrors.zips}
              pageSize={48}
            />
          </Box>
        </>
      ) : (
        ""
      )}
    </ListItem>
  );
}
